import { useMemo, useState } from 'react';
import { groupChannelsBySeries } from '../utils/seriesGrouping';

/**
 * Hook personalizzato per gestire il raggruppamento serie/episodi
 * @param {Array} channels - Lista canali da processare
 * @param {boolean} enableGrouping - Se abilitare il raggruppamento
 * @returns {Object} - Oggetto con canali processati e controlli UI
 */
export function useSeriesGrouping(channels, enableGrouping = true) {
  const [expandedSeries, setExpandedSeries] = useState(new Set());

  // Processa i canali con raggruppamento intelligente
  const processedChannels = useMemo(() => {
    if (!enableGrouping || !channels || channels.length === 0) {
      return { items: channels || [], stats: { totalSeries: 0, totalEpisodes: 0 } };
    }

    const groupedChannels = groupChannelsBySeries(channels);
    const processedItems = [];
    let totalSeries = 0;
    let totalEpisodes = 0;

    groupedChannels.forEach((item) => {
      if (item.type === 'series') {
        // Serie con multipli episodi
        totalSeries++;
        totalEpisodes += item.episodes.length;
        
        // Crea la card serie principale
        const seriesCard = {
          type: 'series',
          seriesKey: `${item.seriesName.toLowerCase()}_s${item.season}`,
          name: `${item.seriesName} (S${item.season})`,
          group: item.group,
          episodeCount: item.episodes.length,
          seasonCount: 1, // Ogni serie è già raggruppata per stagione
          seasons: null, // Non usiamo stagioni multiple qui
          episodes: item.episodes,
          tvgLogo: item.thumbnail,
          originalLogoUrl: item.originalLogoUrl,
          // Per compatibilità con il player, usa il primo episodio come default
          url: item.episodes[0]?.url,
          tvgName: item.episodes[0]?.tvgName
        };

        processedItems.push(seriesCard);

        // Se la serie è espansa, aggiungi gli episodi
        if (expandedSeries.has(seriesCard.seriesKey)) {
          item.episodes.forEach(episode => {
            processedItems.push({
              ...episode,
              type: 'episode',
              seriesKey: seriesCard.seriesKey,
              parentSeries: item.seriesName
            });
          });
        }
      } else {
        // Canal singolo - mostra normalmente
        processedItems.push({
          ...item,
          type: 'channel'
        });
      }
    });

    return {
      items: processedItems,
      stats: { totalSeries, totalEpisodes }
    };
  }, [channels, enableGrouping, expandedSeries]);

  // Funzioni per gestire l'espansione delle serie
  const toggleSeriesExpansion = (seriesKey) => {
    setExpandedSeries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(seriesKey)) {
        newSet.delete(seriesKey);
      } else {
        newSet.add(seriesKey);
      }
      return newSet;
    });
  };

  const expandAllSeries = () => {
    const allSeriesKeys = processedChannels.items
      .filter(item => item.type === 'series')
      .map(item => item.seriesKey);
    setExpandedSeries(new Set(allSeriesKeys));
  };

  const collapseAllSeries = () => {
    setExpandedSeries(new Set());
  };

  const isSeriesExpanded = (seriesKey) => {
    return expandedSeries.has(seriesKey);
  };

  return {
    processedChannels: processedChannels.items,
    seriesStats: processedChannels.stats,
    expandedSeries,
    toggleSeriesExpansion,
    expandAllSeries,
    collapseAllSeries,
    isSeriesExpanded
  };
}
